import {
  COVERAGE_WARN,
  fmt,
  formatByUnit,
  presetRange,
  profitAndLoss,
  todayKey,
  topSellers,
  type RangePreset,
} from '@k2pos/core';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { Mono, Text } from '@/components/ui/Text';
import { cx } from '@/lib/cx';
import { useGoBack } from '@/hooks/useNav';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './PL.module.css';

const PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: 'today', label: 'ມື້ນີ້' },
  { key: 'week', label: '7 ມື້' },
  { key: 'month', label: 'ເດືອນນີ້' },
];

/** ກຳໄລ ຂາດທຶນ — the period summary, from the ledger. */
export function PL() {
  const goBack = useGoBack('/more');
  const journal = usePos((s) => s.journal);
  const journalStartedAt = usePos((s) => s.journalStartedAt);
  const [preset, setPreset] = useState<RangePreset>('month');

  const range = useMemo(() => presetRange(preset, todayKey()), [preset]);
  const pl = useMemo(() => profitAndLoss(journal, range), [journal, range]);
  const sellers = useMemo(() => topSellers(journal, range, 5), [journal, range]);

  // The ledger begins when the app was updated, so a range reaching further back
  // is answering for days it has no data for. Say so rather than imply zero.
  const partial = range.from < journalStartedAt;

  return (
    <>
      <AppBar title="ກຳໄລ ຂາດທຶນ" onBack={goBack} />

      <div className={styles.controls} role="tablist">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={p.key === preset}
            onClick={() => setPreset(p.key)}
            className={cx(styles.chip, p.key === preset && styles.chipOn)}
          >
            <Text
              weight={p.key === preset ? 800 : 500}
              size={11.5}
              color={p.key === preset ? colors.surfaceAlt : colors.textMuted}
            >
              {p.label}
            </Text>
          </button>
        ))}
      </div>

      <div className={cx('vscroll', styles.body)}>
        {partial ? (
          <Card className={styles.notice}>
            <Text size={11.5} color={colors.textMuted}>
              ຂໍ້ມູນເລີ່ມແຕ່ {journalStartedAt} · ກ່ອນໜ້ານັ້ນບໍ່ມີບັນທຶກ
            </Text>
          </Card>
        ) : null}

        {/* Profit block. Accrual: a credit sale counts the day it was sold. */}
        <div className={styles.hero}>
          <Text size={12} color={colors.onDarkBright}>
            ກຳໄລສຸດທິ
          </Text>
          <Mono
            weight={700}
            size={28}
            color={pl.netProfit >= 0 ? colors.sky : '#FFFFFF'}
            className={`${styles.heroAmount} selectable`}
          >
            {fmt(pl.netProfit)}
          </Mono>
          <Text size={11} color={colors.onDarkSoft}>
            ₭ · ນັບຕອນຂາຍ ບໍ່ແມ່ນຕອນເກັບເງິນ
          </Text>

          <div className={styles.heroRows}>
            <HeroRow label="ຍອດຂາຍ" value={pl.revenue} />
            <HeroRow label="ຕົ້ນທຶນຂາຍ" value={-pl.cogs} />
            <HeroRow label="ກຳໄລເບື້ອງຕົ້ນ" value={pl.grossProfit} strong />
            <HeroRow label="ລາຍຈ່າຍ" value={-pl.expenses} />
          </div>
        </div>

        {pl.coverage < COVERAGE_WARN && pl.revenue > 0 ? (
          <Link to="/stock" className={styles.warnLink}>
            <Card className={styles.warn}>
              <div className="grow col">
                <Text weight={800} size={12} color={colors.skyDeep}>
                  ນັບຕົ້ນທຶນໄດ້ພຽງ {Math.round(pl.coverage * 100)}% ຂອງຍອດຂາຍ
                </Text>
                <Text size={10.5} color={colors.textMuted}>
                  ໃສ່ຕົ້ນທຶນໃຫ້ຄົບ ກຳໄລຈຶ່ງຈະຖືກຕ້ອງ · ດຽວນີ້ສູງເກີນຈິງ
                </Text>
              </div>
            </Card>
          </Link>
        ) : null}

        {/* The single most likely thing to be misread, so it is labelled. */}
        {pl.purchases > 0 ? (
          <Card className={styles.row}>
            <div className="grow col">
              <Text size={12.5} color={colors.textMuted}>
                ຊື້ເຄື່ອງເຂົ້າ
              </Text>
              <Text size={10} color={colors.textFaint}>
                ບໍ່ນັບເປັນລາຍຈ່າຍ · ຈະນັບເປັນຕົ້ນທຶນເມື່ອຂາຍອອກ
              </Text>
            </div>
            <Mono weight={700} size={14}>
              {fmt(pl.purchases)}
            </Mono>
          </Card>
        ) : null}

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ເງິນສົດ
        </Text>
        <Card className={styles.cash}>
          <CashRow label="ເງິນເຂົ້າ" value={pl.cashIn} />
          <CashRow label="ເງິນອອກ" value={-pl.cashOut} />
          <div className={styles.dash} />
          <div className={styles.row}>
            <Text weight={800} size={14} className="grow">
              ເງິນສົດສຸດທິ
            </Text>
            <Mono
              weight={700}
              size={17}
              color={pl.cashNet >= 0 ? colors.skyDeep : colors.navy}
              className="selectable"
            >
              {fmt(pl.cashNet)} ₭
            </Mono>
          </div>
          {/* Profit and cash diverge whenever the shop sells on credit or
              restocks, which for this product is most of the time. */}
          <Text size={10} color={colors.textFaint}>
            ຕ່າງຈາກກຳໄລ ເພາະຂາຍຕິດຍັງບໍ່ໄດ້ເງິນ ແລະ ຊື້ເຄື່ອງເສຍເງິນກ່ອນຂາຍ
          </Text>
        </Card>

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ຂາຍດີສຸດ
        </Text>
        {sellers.map((s, i) => (
          <Card key={s.productId} className={styles.sellerRow}>
            <Mono weight={700} size={12} color={colors.textFaint} className={styles.rank}>
              {i + 1}
            </Mono>
            <div className="grow col">
              <Text weight={700} size={13} lines={1}>
                {s.name}
              </Text>
              {/* Quantities stay per unit: 48 ຖົງ plus 0.5 ໂຕນ is not 48.5 of
                  anything, the same rule the cart badge follows. */}
              <Mono size={10.5} color={colors.textMuted}>
                {formatByUnit(s.byUnit)}
              </Mono>
            </div>
            <Mono weight={700} size={13}>
              {fmt(s.amount)}
            </Mono>
          </Card>
        ))}

        {sellers.length === 0 ? (
          <Text size={12} color={colors.textMuted} align="center" className={styles.empty}>
            ຍັງບໍ່ມີການຂາຍໃນຊ່ວງນີ້
          </Text>
        ) : null}

        <Mono size={10.5} color={colors.textFaint} align="center" className={styles.footer}>
          {pl.bills} ບິນ · {range.from} → {range.to}
        </Mono>
      </div>
    </>
  );
}

function HeroRow({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <div className={styles.heroRow}>
      <Text
        size={11.5}
        weight={strong ? 800 : 400}
        color={strong ? colors.onDark : colors.onDarkSoft}
        className="grow"
      >
        {label}
      </Text>
      <Mono weight={strong ? 700 : 400} size={12.5} color={colors.onDark}>
        {fmt(value)}
      </Mono>
    </div>
  );
}

function CashRow({ label, value }: { label: string; value: number }) {
  return (
    <div className={styles.row}>
      <Text size={12.5} color={colors.textMuted} className="grow">
        {label}
      </Text>
      <Mono weight={700} size={13.5}>
        {fmt(value)}
      </Mono>
    </div>
  );
}
